import { useEffect, useMemo, useState } from 'react';
import { App, Alert, Button, Modal, Radio, Skeleton, Space, Table, Tag, Typography } from 'antd';
import { LoadError } from '../../components/states';
import { fmtDate, plural } from '../../utils/format';
import { REASON_LABELS, type MergeFieldRow } from '@crm/shared';
import dayjs from 'dayjs';
import { errorMessage } from '../../api/client';
import { palette } from '../../app/theme';
import { useDuplicateCompare, useMergeCandidate } from '../../features/dedupe/api';

const fmt = (v: unknown) => (v === null || v === undefined || v === '' ? <Typography.Text type="secondary">—</Typography.Text> : Array.isArray(v) ? v.join(', ') : typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v));

/** Side-by-side merge: pick the surviving record and, per field, which value to keep. Everything else is combined. */
export function MergeDialog({ candidateId, onClose }: { candidateId: string | null; onClose: () => void }) {
  const { message } = App.useApp();
  const cmp = useDuplicateCompare(candidateId ?? undefined);
  const merge = useMergeCandidate();
  const [survivor, setSurvivor] = useState<'a' | 'b'>('a');
  const [choices, setChoices] = useState<Record<string, 'a' | 'b'>>({});

  useEffect(() => {
    if (!cmp.data) return;
    // default survivor: the older record; defaults per field follow the server suggestion
    const older = dayjs(cmp.data.candidate.a.createdAt).isAfter(cmp.data.candidate.b.createdAt) ? 'b' : 'a';
    setSurvivor(older);
    setChoices(Object.fromEntries(cmp.data.fields.map((f) => [f.key, f.suggested])));
  }, [cmp.data]);

  const rows = useMemo(() => cmp.data?.fields.filter((f) => !(f.a === null && f.b === null)) ?? [], [cmp.data]);
  const c = cmp.data?.candidate;

  const submit = async () => {
    if (!candidateId) return;
    try {
      await merge.mutateAsync({ id: candidateId, survivor, fields: choices });
      message.success('Merged. The retired record now points to the survivor and all history moved over.');
      onClose();
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  return (
    <Modal
      title="Merge duplicates"
      open={!!candidateId}
      onCancel={onClose}
      width={880}
      destroyOnHidden
      footer={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" onClick={() => void submit()} loading={merge.isPending} disabled={!cmp.data}>{c ? `Merge into ${survivor === 'a' ? c.a.label : c.b.label}` : 'Merge'}</Button>
        </Space>
      }
    >
      {cmp.isError ? (
        <LoadError error={cmp.error} onRetry={cmp.refetch} title="Could not compare these records" />
      ) : !cmp.data || !c ? (
        <Skeleton active />
      ) : (
        <>
          <Space wrap style={{ marginBottom: 12 }}>
            {c.reasons.map((r) => <Tag key={r} color="gold">{REASON_LABELS[r] ?? r}</Tag>)}
            <Typography.Text type="secondary">Match score {Math.round(c.score * 100)}%</Typography.Text>
          </Space>
          <Radio.Group value={survivor} onChange={(e) => setSurvivor(e.target.value)} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            {(['a', 'b'] as const).map((side) => {
              const s = c[side];
              return (
                <Radio key={side} value={side} style={{ border: `1px solid ${survivor === side ? palette.primary : palette.border}`, background: survivor === side ? palette.primarySoft : undefined, borderRadius: 12, padding: 14, margin: 0 }}>
                  <Typography.Text strong>{s.label}</Typography.Text> {survivor === side && <Tag color="blue">Survives</Tag>}
                  <div><Typography.Text type="secondary" style={{ fontSize: 12 }}>{[s.email ?? s.domain, `created ${fmtDate(s.createdAt)}`, plural(s.counts.deals, 'deal'), plural(s.counts.activities, 'history item')].filter(Boolean).join(' · ')}</Typography.Text></div>
                </Radio>
              );
            })}
          </Radio.Group>
          <Table<MergeFieldRow>
            rowKey="key"
            size="small"
            pagination={false}
            dataSource={rows}
            scroll={{ y: 320 }}
            columns={[
              { title: 'Field', dataIndex: 'label', width: 160 },
              {
                title: c.a.label,
                key: 'a',
                render: (_, r) => <Radio checked={(choices[r.key] ?? r.suggested) === 'a'} onChange={() => setChoices({ ...choices, [r.key]: 'a' })}>{fmt(r.a)}</Radio>,
              },
              {
                title: c.b.label,
                key: 'b',
                render: (_, r) => <Radio checked={(choices[r.key] ?? r.suggested) === 'b'} onChange={() => setChoices({ ...choices, [r.key]: 'b' })}>{fmt(r.b)}</Radio>,
              },
            ]}
          />
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 12 }}
            title="Nothing is lost"
            description={
              <span>
                Combined on the survivor: {plural(cmp.data.combined.emails.length, 'email address', 'email addresses')}{cmp.data.combined.organizations?.length ? `, organizations ${cmp.data.combined.organizations.join(', ')}` : ''}{cmp.data.combined.tags.length ? `, tags ${cmp.data.combined.tags.join(', ')}` : ''}{cmp.data.combined.lists.length ? `, lists ${cmp.data.combined.lists.join(', ')}` : ''}, {plural(cmp.data.combined.deals, 'deal')}, {plural(cmp.data.combined.tasks, 'task')}, {plural(cmp.data.combined.activities, 'history item')}. A full snapshot of the retired record is kept on the timeline, and mail to its addresses still reaches the survivor.
              </span>
            }
          />
        </>
      )}
    </Modal>
  );
}
